import axios from 'axios';

/**
 * Deletes a document from the knowledge base
 * @param id The MongoDB id of the document to delete
 * @returns A Promise that resolves to true if deletion was successful, false otherwise
 */
export const deleteDocumentKnowledge = async (id: string): Promise<boolean> => {
  try {
    const token = localStorage.getItem('authToken');
    if (!token) {
      console.error('No JWT token found in localStorage');
      return false;
    }

    // Clean the ID by removing any quotes
    const cleanId = id.replace(/["']/g, '');

    const baseUrl = process.env.REACT_APP_API_BASE_URL;
    const serverType = process.env.REACT_APP_SERVER_TYPE;
    const webhookPath = serverType === 'test' ? 'webhook-test' : 'webhook';

    // Construct the delete URL with the document ID
    const deleteUrl = `${baseUrl}/${webhookPath}/agent_gg_aukik/delete`;

    // Set up the query parameters
    const params = {
      route: 'document_knowledge',
      id: cleanId
    };

    // Make the DELETE request
    const response = await axios.delete(deleteUrl, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      params
    });

    console.log('Document successfully deleted:', response.data);
    return true;
  } catch (error) {
    console.error('Error deleting document:', error);
    return false;
  }
};

/**
 * Fetches the PDF view URL for a document
 * @param id The MongoDB id of the document
 * @returns A Promise that resolves to the PDF view URL
 */
export const getPdfViewUrl = async (id: string): Promise<string | null> => {
  try {
    console.log(`getPdfViewUrl: Fetching PDF URL for document ID: ${id}`);

    const token = localStorage.getItem('authToken');
    if (!token) {
      console.error('No JWT token found in localStorage');
      return null;
    }

    // Clean the ID by removing any quotes
    const cleanId = id.replace(/["']/g, '');
    console.log(`getPdfViewUrl: Using cleaned ID: ${cleanId}`);

    const baseUrl = process.env.REACT_APP_API_BASE_URL;
    const serverType = process.env.REACT_APP_SERVER_TYPE;
    const webhookPath = serverType === 'test' ? 'webhook-test' : 'webhook';

    // Construct the URL to fetch the PDF URL
    const pdfUrlEndpoint = `${baseUrl}/${webhookPath}/agent_gg_aukik/get`;
    console.log(`getPdfViewUrl: Using endpoint: ${pdfUrlEndpoint}`);

    // Set up the parameters
    const params = {
      route: 'pdf_url',
      id: cleanId
    };

    // Make the GET request
    console.log(`getPdfViewUrl: Making request with params:`, params);
    const response = await axios.get(pdfUrlEndpoint, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      params
    });

    console.log(`getPdfViewUrl: Got response:`, response.data);

    if (response.data && response.data.url) {
      // Process the URL
      let url = response.data.url.trim();
      if (url.startsWith('@')) url = url.substring(1);

      // Handle Dropbox URLs
      if (url.includes('dropbox.com')) {
        // Convert to direct download format
        url = url.replace('www.dropbox.com', 'dl.dropboxusercontent.com');

        // Remove dl parameter as it's not needed with the direct download domain
        url = url.replace(/[?&]dl=\d/g, '');
      } else {
        // For non-Dropbox URLs, ensure the URL has dl=1 parameter for viewing
        if (url.includes('dl=0')) {
          url = url.replace('dl=0', 'dl=1');
        } else if (!url.includes('dl=')) {
          url += url.includes('?') ? '&dl=1' : '?dl=1';
        }
      }

      console.log(`getPdfViewUrl: Returning final URL: ${url}`);
      return url;
    }

    console.log(`getPdfViewUrl: No URL found in response`);
    return null;
  } catch (error) {
    console.error('Error fetching PDF view URL:', error);
    return null;
  }
};

/**
 * Fetches the PDF download URL for a document
 * @param id The MongoDB id of the document
 * @returns A Promise that resolves to the PDF download URL
 */
export const getPdfDownloadUrl = async (id: string): Promise<string | null> => {
  try {
    const token = localStorage.getItem('authToken');
    if (!token) {
      console.error('No JWT token found in localStorage');
      return null;
    }

    // Clean the ID by removing any quotes
    const cleanId = id.replace(/["']/g, '');

    const baseUrl = process.env.REACT_APP_API_BASE_URL;
    const serverType = process.env.REACT_APP_SERVER_TYPE;
    const webhookPath = serverType === 'test' ? 'webhook-test' : 'webhook';

    // Construct the direct download URL
    return `${baseUrl}/${webhookPath}/agent_gg_aukik/get?route=pdf_url&id=${cleanId}`;
  } catch (error) {
    console.error('Error generating PDF download URL:', error);
    return null;
  }
};
